// Thin fetch wrapper for /api/admin/rules endpoints.
// No business logic — just HTTP calls via the admin httpClient.
//
// LLD 11 v0.2: rules carry two scope axes (location + device), each a
// `{ mode, ids }` shape with mode ∈ {ALL, ALLOW, DENY}. The wire format
// uses nested objects directly — no flattening — and the `priority` field
// is gone.

import {
  adminGet,
  adminPost,
  adminPatch,
  adminDelete,
} from "@/data/httpClient";
import { unwrapEnvelope } from "@/data/unwrapEnvelope";
import type { ReconcileSummary, AliasFailure, AuditEntry } from "@/types/role";
import type {
  Rule,
  GroupMatch,
  LocationScope,
  DeviceScope,
  RuleUser,
  GroupMember,
  ValidateRuleRequest,
} from "@/types/rule";

/** One projected grant delta from a rule preview. */
export interface PreviewGrant {
  alias: string;
  role_id: string;
  location_id?: string;
}

/** One device matched by a predicate device-scope preview (LLD 12 §4.4). */
export interface PreviewMatchedDevice {
  doorbot_id: string;
  name: string;
}

/**
 * Device sub-preview for a predicate device scope (LLD 12 §4.4). Present only
 * when the previewed rule carries a `device_scope.predicate`; absent for
 * ids-only / ALL scopes. `matched_devices` is a point-in-time estimate of a
 * LIVE set — never authoritative — and `truncated` is a BLOCKING warning that
 * the scan hit `PREVIEW_MAX_SCANNED_DEVICES` / `PREVIEW_SCAN_BUDGET_MS` before
 * enumerating the full fleet.
 */
export interface PreviewDeviceSummary {
  matched_devices: PreviewMatchedDevice[];
  scanned_device_count: number;
  match_rate: number;
  truncated: boolean;
}

/** Response of POST /api/admin/rules:preview — a dry-run reconciliation. */
export interface PreviewRuleResponse {
  summary: {
    affected_alias_count: number;
    grants_added: PreviewGrant[];
    grants_removed: PreviewGrant[];
  };
  /** Device match summary; present only for a predicate device scope. */
  device_preview?: PreviewDeviceSummary;
}

export interface RuleClient {
  list(params: {
    cursor?: string;
    limit?: number;
    search?: string;
    enabled?: boolean;
    role_id?: string;
  }): Promise<{ items: Rule[]; next_cursor?: string }>;

  get(
    ruleId: string,
  ): Promise<Rule & { users_count: number; group_member_count: number }>;

  create(req: {
    rule_id: string;
    display_name: string;
    description: string;
    group_match: GroupMatch;
    role_id: string;
    location_scope: LocationScope;
    device_scope: DeviceScope;
    enabled?: boolean;
  }): Promise<{ rule: Rule; summary: ReconcileSummary }>;

  update(
    ruleId: string,
    patch: Partial<Omit<Rule, "rule_id" | "created_at" | "version">>,
    version: number,
  ): Promise<{ rule: Rule; summary: ReconcileSummary; partial?: AliasFailure[] }>;

  delete(
    ruleId: string,
    version: number,
  ): Promise<{ rule_id: string; deleted_at: string; summary: ReconcileSummary }>;

  validate(
    body: ValidateRuleRequest,
  ): Promise<{ ok: boolean; errors?: string[] }>;

  /**
   * Dry-run a rule against current group membership without persisting it.
   * Mirrors validate() but hits POST /api/admin/rules:preview and returns the
   * projected reconciliation: how many aliases would be affected and the
   * grant deltas. Used to show "N users will receive this role" in the form.
   */
  preview(
    body: ValidateRuleRequest,
  ): Promise<PreviewRuleResponse>;

  reconcile(
    ruleId: string,
  ): Promise<{ summary: ReconcileSummary; partial?: AliasFailure[] }>;

  listUsers(
    ruleId: string,
    cursor?: string,
  ): Promise<{ items: RuleUser[]; next_cursor?: string }>;

  listGroupMembers(
    ruleId: string,
    cursor?: string,
  ): Promise<{ items: GroupMember[]; next_cursor?: string }>;

  listActivity(
    ruleId: string,
    cursor?: string,
  ): Promise<{ items: AuditEntry[]; next_cursor?: string }>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    (pair): pair is [string, string | number | boolean] => pair[1] !== undefined,
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  ).toString();
  return `?${qs}`;
}

/**
 * Normalize a rule from the backend into the v0.2 nested shape.
 *
 * Tolerant inputs:
 *   - Pre-v0.2 BFF emits flat `group_match_*` and `location_scope_*`
 *     fields, no `device_scope`. We synthesize defaults:
 *       location_scope.mode === "all_tenant_locations" → { mode: "ALL", ids: [] }
 *       location_scope.mode === "fixed_list"           → { mode: "ALLOW", ids: [...] }
 *       device_scope                                   → { mode: "ALL", ids: [] }
 *   - v0.2 BFF emits the nested shape directly; we pass it through.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeRule(raw: any): Rule {
  const rule: Record<string, unknown> = { ...raw };

  // group_match flat → nested
  if (
    !rule.group_match &&
    (rule.group_match_type || rule.group_match_group_name)
  ) {
    rule.group_match = {
      type: rule.group_match_type ?? "exact",
      group_name: rule.group_match_group_name ?? "",
    };
    delete rule.group_match_type;
    delete rule.group_match_group_name;
  }

  // location_scope: legacy flat, legacy mode names, or v0.2 nested
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ls: any = rule.location_scope;
  if (!ls && rule.location_scope_mode) {
    rule.location_scope = legacyToScope(
      String(rule.location_scope_mode),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rule as any).location_scope_location_ids ?? [],
    );
    delete rule.location_scope_mode;
    delete rule.location_scope_location_ids;
  } else if (ls && typeof ls === "object") {
    // v0.2 mode passes through; legacy "all_tenant_locations" / "fixed_list"
    // gets translated.
    if (ls.mode === "all_tenant_locations") {
      rule.location_scope = { mode: "ALL", ids: [] };
    } else if (ls.mode === "fixed_list") {
      rule.location_scope = {
        mode: "ALLOW",
        ids: Array.isArray(ls.location_ids) ? [...ls.location_ids] : [],
      };
    } else {
      rule.location_scope = {
        mode: ls.mode,
        ids: Array.isArray(ls.ids) ? [...ls.ids] : [],
      };
    }
  } else if (!ls) {
    rule.location_scope = { mode: "ALL", ids: [] };
  }

  // device_scope: v0.2 only — older BFFs default to ALL. LLD 12: a predicate
  // selector (device_scope.predicate) passes through verbatim; it is mutually
  // exclusive with a non-empty `ids`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ds: any = rule.device_scope;
  if (!ds) {
    rule.device_scope = { mode: "ALL", ids: [] };
  } else if (typeof ds === "object") {
    const next: Record<string, unknown> = {
      mode: ds.mode ?? "ALL",
      ids: Array.isArray(ds.ids) ? [...ds.ids] : [],
    };
    // Carry the predicate through unchanged when present (LLD 12 wire shape:
    // { join, conditions: [{ field, op, value, negate?, case_insensitive? }] }).
    if (ds.predicate && typeof ds.predicate === "object") {
      next.predicate = ds.predicate;
    }
    rule.device_scope = next;
  }

  return rule as unknown as Rule;
}

function legacyToScope(
  mode: string,
  ids: string[],
): { mode: "ALL" | "ALLOW" | "DENY"; ids: string[] } {
  if (mode === "all_tenant_locations" || mode === "ALL") {
    return { mode: "ALL", ids: [] };
  }
  if (mode === "fixed_list" || mode === "ALLOW") {
    return { mode: "ALLOW", ids: [...ids] };
  }
  if (mode === "DENY") {
    return { mode: "DENY", ids: [...ids] };
  }
  return { mode: "ALL", ids: [] };
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

function createRuleClient(): RuleClient {
  const BASE = "/api/admin/rules";

  return {
    list(params) {
      const qs = buildQuery(params);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return adminGet<{ rules?: any[]; items?: any[]; next_cursor?: string }>(
        `${BASE}${qs}`,
      ).then((resp) => ({
        items: (resp.rules ?? resp.items ?? []).map(normalizeRule),
        next_cursor: resp.next_cursor,
      }));
    },

    get(ruleId) {
      type GetRuleResp = Rule & {
        users_count: number;
        group_member_count: number;
      };
      // Single-resource GET is enveloped as {"rule": {...}} with the rule's
      // group_match in the FLAT (group_match_group_name) encoding. Unwrap the
      // envelope first, THEN normalize — running normalizeRule on the raw
      // envelope left group_match undefined and crashed the detail page on
      // `rule.group_match.type`.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return adminGet<any>(
        `${BASE}/${encodeURIComponent(ruleId)}`,
      ).then((raw) => normalizeRule(unwrapEnvelope(raw, "rule")) as GetRuleResp);
    },

    create(req) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return adminPost(BASE, req).then((resp: any) => ({
        ...resp,
        rule: resp.rule ? normalizeRule(resp.rule) : normalizeRule(resp),
      }));
    },

    update(ruleId, patch, version) {
      return adminPatch(
        `${BASE}/${encodeURIComponent(ruleId)}`,
        patch,
        version,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ).then((resp: any) => ({
        ...resp,
        rule: resp.rule ? normalizeRule(resp.rule) : normalizeRule(resp),
      }));
    },

    delete(ruleId, version) {
      return adminDelete(
        `${BASE}/${encodeURIComponent(ruleId)}`,
        version,
      );
    },

    validate(body) {
      return adminPost(`${BASE}:validate`, body);
    },

    preview(body) {
      return adminPost(`${BASE}:preview`, body);
    },

    reconcile(ruleId) {
      return adminPost(
        `${BASE}/${encodeURIComponent(ruleId)}/reconcile`,
      );
    },

    listUsers(ruleId, cursor) {
      const qs = buildQuery({ cursor });
      return adminGet<{ items?: RuleUser[]; users?: RuleUser[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(ruleId)}/users${qs}`,
      ).then((resp) => ({
        items: resp.items ?? resp.users ?? [],
        next_cursor: resp.next_cursor,
      }));
    },

    listGroupMembers(ruleId, cursor) {
      const qs = buildQuery({ cursor });
      return adminGet<{ items?: GroupMember[]; members?: GroupMember[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(ruleId)}/group-members${qs}`,
      ).then((resp) => ({
        items: resp.items ?? resp.members ?? [],
        next_cursor: resp.next_cursor,
      }));
    },

    listActivity(ruleId, cursor) {
      const qs = buildQuery({ cursor });
      return adminGet<{ items?: AuditEntry[]; activity?: AuditEntry[]; next_cursor?: string }>(
        `${BASE}/${encodeURIComponent(ruleId)}/activity${qs}`,
      ).then((resp) => ({
        items: resp.items ?? resp.activity ?? [],
        next_cursor: resp.next_cursor,
      }));
    },
  };
}

/** Singleton rule client instance. */
export const ruleClient: RuleClient = createRuleClient();
